import React, { useCallback, useMemo } from 'react';

import {
  Dialog,
  DialogProps,
  DialogTitle,
  DialogTitleProps,
  IconButton,
  Slide,
  Typography,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import { styled } from '@material-ui/core/styles';
import { Breakpoint } from '@material-ui/core/styles/createBreakpoints';
import { TransitionProps } from '@material-ui/core/transitions';
import { Close } from '@material-ui/icons';

const DialogTransition = React.forwardRef(
  (
    props: TransitionProps & { children?: React.ReactElement<any, any> },
    ref: React.Ref<unknown>
  ) => <Slide direction='up' ref={ref} {...props} />
);
interface WrappedProps {
  onClose: () => void;
  classNames?: string;
}

export interface WithDialogProps {
  open: boolean;
}

export interface WithDialogOptions {
  maxWidth?: DialogProps['maxWidth'];
  fullWidth?: boolean;
  fullScreen?: boolean;
  fullScreenBreakpoint?: Breakpoint | number;
}

export const withDialog = <TProps extends WrappedProps>(
  Component: React.FC<TProps>,
  options: WithDialogOptions = {}
): React.FC<TProps & WithDialogProps> =>
  React.memo((props) => {
    const { open, onClose } = props;

    const theme = useTheme();
    const fullScreenQuery = useMediaQuery(
      theme.breakpoints.down(options.fullScreenBreakpoint ?? 'xs')
    );

    const fullScreen = useMemo(
      () => options.fullScreen || fullScreenQuery,
      [fullScreenQuery]
    );

    const handleClose = useCallback(() => {
      onClose();
    }, [onClose]);

    return (
      <Dialog
        open={open}
        onClose={handleClose}
        TransitionComponent={DialogTransition}
        fullScreen={fullScreen}
        fullWidth={options.fullWidth}
        maxWidth={options.maxWidth}
      >
        <Component {...props} />
      </Dialog>
    );
  });

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  margin: 0,
  padding: theme.spacing(1),
  display: 'flex',
}));

const StyledTypography = styled(Typography)({
  display: 'flex',
  justifyContent: 'center',
  width: '100%',
});

const StyledCloseButton = styled(IconButton)({
  marginLeft: 'auto',
});

interface MetDialogTitleProps extends DialogTitleProps {
  onClose?: () => void;
}

export const MetDialogTitle: React.FC<MetDialogTitleProps> = ({
  onClose,
  children,
  ...other
}) => {
  return (
    <StyledDialogTitle disableTypography {...other}>
      <StyledTypography variant='h6'>{children}</StyledTypography>
      {onClose && (
        <StyledCloseButton onClick={onClose}>
          <Close />
        </StyledCloseButton>
      )}
    </StyledDialogTitle>
  );
};
